import React, { useState } from "react";
import {
  Box,
  ClickAwayListener,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";

import { flexRender, Header } from "@tanstack/react-table";
import {
  ArrowDownAZIcon,
  ArrowDownUpIcon,
  ArrowDownZAIcon,
  InfoIcon,
} from "lucide-react";

import { VtColumnMeta } from "./DataTable";

function getSortIcons() {
  return {
    asc: <ArrowDownAZIcon size={16} style={{ minWidth: 16, minHeight: 16 }} />,
    desc: <ArrowDownZAIcon size={16} style={{ minWidth: 16, minHeight: 16 }} />,
    default: (
      <Box sx={{ lineHeight: 0, color: "texts.neutral.decoration" }}>
        <ArrowDownUpIcon size={16} style={{ minWidth: 16, minHeight: 16 }} />
      </Box>
    ),
  };
}

const HeaderCell = <TData,>({ header }: { header: Header<TData, unknown> }) => {
  const sortIcons = getSortIcons();
  const [open, setOpen] = useState(false);

  const meta = header.column.columnDef.meta as VtColumnMeta;

  const tooltip = meta?.tooltip;
  const isSortable = header.column.getCanSort();
  const sortIcon = sortIcons[header.column.getIsSorted() || "default"];

  const toggleTooltip = () => setOpen((open) => !open);

  return (
    <TableCell
      sx={{
        borderWidth: 0,
        borderRightWidth: header.column.getIsLastColumn() ? 0 : 1,
        borderRightStyle: "solid",
        borderRightColor: "backgrounds.input.default",
        backgroundColor: "backgrounds.neutral.moderate.default",
        padding: 1.5,
        verticalAlign: "top",
      }}
    >
      {header.isPlaceholder ? null : (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Tooltip placement="top" open={open} title={tooltip}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                cursor: isSortable || tooltip ? "pointer" : "default",
                gap: 0.5,
                color: "texts.neutral.subtle",
              }}
              onClick={(event) => {
                if (isSortable) {
                  header.column.getToggleSortingHandler()?.(event);
                } else if (tooltip) {
                  toggleTooltip();
                }
              }}
            >
              <Typography
                variant={"bodyMicroRegular"}
                sx={{ textTransform: "uppercase" }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </Typography>

              {tooltip && (
                <InfoIcon size={18} style={{ minWidth: 18, minHeight: 18 }} />
              )}

              {isSortable && !tooltip && sortIcon}
            </Box>
          </Tooltip>
        </ClickAwayListener>
      )}
    </TableCell>
  );
};

export default HeaderCell;
